import React, { useState, useCallback, useEffect } from 'react'
import { motion } from 'framer-motion'
import { useTranslation } from 'react-i18next'
import { useParams, useNavigate } from 'react-router-dom'
import DashboardLayout from '../components/layout/DashboardLayout'
import Card from '../components/common/Card'
import Button from '../components/common/Button'
import Input from '../components/common/Input'
import Modal from '../components/common/Modal'
import Loading from '../components/common/Loading'
import { useFetch } from '../hooks/useFetch'
import { apiService } from '../services/api'
import type { Form, FormField, FormStructure, FormSettings } from '../types'

const FormEditor: React.FC = () => {
  const { t } = useTranslation()
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const isEditMode = Boolean(id)

  const [formData, setFormData] = useState<Form>({
    id: 0,
    user_id: 0,
    name: '',
    description: '',
    structure: {
      fields: [],
      layout: 'vertical',
      submitButton: {
        text: t('submit'),
        style: 'primary',
        position: 'center'
      }
    },
    status: 'draft',
    settings: {
      allowMultipleSubmissions: false,
      requireLogin: false,
      showProgressBar: true,
      confirmationMessage: t('formSubmittedSuccessfully')
    },
    view_count: 0,
    submit_count: 0,
    created_at: '',
    updated_at: ''
  })
  const [isFieldModalOpen, setIsFieldModalOpen] = useState(false)
  const [editingField, setEditingField] = useState<FormField | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)

  // 获取表单数据（编辑模式）
  const { data: formResponse, isLoading } = useFetch<{ form: Form }>(
    async () => {
      if (!isEditMode) return { form: formData }
      const response = await apiService.getFormById(Number(id))
      if (response.error) throw new Error(response.error)
      return response.data || { form: formData }
    },
    { initialData: { form: formData } }
  )

  // 加载表单数据
  useEffect(() => {
    if (formResponse?.form) {
      setFormData(formResponse.form)
    }
  }, [formResponse])

  // 处理保存表单
  const handleSave = useCallback(async () => {
    if (!formData.name) {
      alert(t('pleaseEnterFormTitle'))
      return
    }

    setIsSubmitting(true)
    try {
      let response
      if (isEditMode) {
        response = await apiService.updateForm(Number(id), formData)
      } else {
        response = await apiService.createForm(formData)
      }

      if (!response.error) {
        navigate('/forms')
      }
    } finally {
      setIsSubmitting(false)
    }
  }, [formData, isEditMode, id, navigate, t])

  // 处理添加/编辑字段
  const handleSaveField = useCallback((field: FormField) => {
    const fields = [...(formData.structure?.fields || [])]
    
    if (editingField) {
      // 编辑现有字段
      const index = fields.findIndex((f: FormField) => f.id === editingField.id)
      if (index !== -1) {
        fields[index] = field
      }
    } else {
      // 添加新字段
      field.id = Date.now().toString()
      fields.push(field)
    }

    setFormData({ 
      ...formData, 
      structure: { 
        ...formData.structure, 
        fields 
      } 
    })
    setIsFieldModalOpen(false)
    setEditingField(null)
  }, [formData, editingField])

  // 处理删除字段
  const handleDeleteField = useCallback((fieldId: string) => {
    const fields = (formData.structure?.fields || []).filter((f: FormField) => f.id !== fieldId)
    setFormData({ 
      ...formData, 
      structure: { 
        ...formData.structure, 
        fields 
      } 
    })
  }, [formData])

  // 处理字段排序
  const handleMoveField = useCallback((index: number, direction: 'up' | 'down') => {
    const fields = [...(formData.structure?.fields || [])]
    const newIndex = direction === 'up' ? index - 1 : index + 1

    if (newIndex < 0 || newIndex >= fields.length) return

    const temp = fields[index]
    fields[index] = fields[newIndex]
    fields[newIndex] = temp

    setFormData({ 
      ...formData, 
      structure: { 
        ...formData.structure, 
        fields 
      } 
    })
  }, [formData])

  // 字段类型选项
  const fieldTypes = [
    { value: 'text', label: t('text'), icon: '' },
    { value: 'email', label: t('email'), icon: '' },
    { value: 'tel', label: t('phone'), icon: '' },
    { value: 'number', label: t('number'), icon: '' },
    { value: 'textarea', label: t('textarea'), icon: '' },
    { value: 'select', label: t('select'), icon: '' },
    { value: 'radio', label: t('radio'), icon: '' },
    { value: 'checkbox', label: t('checkbox'), icon: '' },
    { value: 'date', label: t('date'), icon: '' },
    { value: 'time', label: t('time'), icon: '' },
    { value: 'datetime', label: t('datetime'), icon: '' },
    { value: 'file', label: t('file'), icon: '' },
    { value: 'rating', label: t('rating'), icon: '' },
  ]

  if (isLoading) {
    return (
      <DashboardLayout title={isEditMode ? t('editForm') : t('createForm')}>
        <div className="flex items-center justify-center h-96">
          <Loading size="lg" />
        </div>
      </DashboardLayout>
    )
  }

  return (
    <DashboardLayout 
      title={isEditMode ? t('editForm') : t('createForm')}
      actions={
        <div className="flex items-center space-x-2">
          <Button
            variant="secondary"
            onClick={() => navigate('/forms')}
          >
            {t('cancel')}
          </Button>
          <Button
            variant="primary"
            onClick={handleSave}
            loading={isSubmitting}
          >
            {t('save')}
          </Button>
        </div>
      }
    >
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* 左侧：表单设置 */}
        <div className="lg:col-span-1 space-y-6">
          <Card>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  {t('formTitle')} *
                </label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder={t('enterFormTitle')}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  {t('description')}
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder={t('enterFormDescription')}
                  rows={3}
                  className="w-full px-4 py-2 rounded-lg bg-dark/50 border border-gray-700 focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none resize-none"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  {t('status')}
                </label>
                <select
                  value={formData.status}
                  onChange={(e) => setFormData({ ...formData, status: e.target.value as Form['status'] })}
                  className="w-full px-4 py-2 rounded-lg bg-dark/50 border border-gray-700 focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none"
                >
                  <option value="draft">{t('draft')}</option>
                  <option value="published">{t('published')}</option>
                  <option value="disabled">{t('disabled')}</option>
                  <option value="archived">{t('archived')}</option>
                </select>
              </div>
            </div>
          </Card>

          <Card>
            <h4 className="font-medium mb-4">{t('formSettings')}</h4>
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-300">{t('allowMultipleSubmissions')}</span>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.settings?.allowMultipleSubmissions}
                    onChange={(e) => setFormData({
                      ...formData,
                      settings: { ...formData.settings, allowMultipleSubmissions: e.target.checked }
                    })}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-700 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary/20 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
                </label>
              </div>

              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-300">{t('requireLogin')}</span>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.settings?.requireLogin}
                    onChange={(e) => setFormData({
                      ...formData,
                      settings: { ...formData.settings, requireLogin: e.target.checked }
                    })}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-700 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary/20 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
                </label>
              </div>

              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-300">{t('showProgressBar')}</span>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.settings?.showProgressBar}
                    onChange={(e) => setFormData({
                      ...formData,
                      settings: { ...formData.settings, showProgressBar: e.target.checked }
                    })}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-700 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary/20 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
                </label>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  {t('confirmationMessage')}
                </label>
                <textarea
                  value={formData.settings?.confirmationMessage}
                  onChange={(e) => setFormData({
                    ...formData,
                    settings: { ...formData.settings, confirmationMessage: e.target.value }
                  })}
                  rows={2}
                  className="w-full px-4 py-2 rounded-lg bg-dark/50 border border-gray-700 focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none resize-none text-sm"
                />
              </div>
            </div>
          </Card>
        </div>

        {/* 右侧：字段编辑器 */}
        <div className="lg:col-span-2">
          <Card>
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-semibold">{t('formFields')}</h3>
              <Button
                variant="primary"
                size="sm"
                onClick={() => {
                  setEditingField(null)
                  setIsFieldModalOpen(true)
                }}
              >
                {t('addField')}
              </Button>
            </div>

            {formData.structure?.fields?.length === 0 ? (
              <div className="text-center py-12 text-gray-400">
                <div className="text-4xl mb-4"></div>
                <p>{t('noFieldsYet')}</p>
                <p className="text-sm mt-2">{t('clickAddFieldToStart')}</p>
              </div>
            ) : (
              <div className="space-y-3">
                {formData.structure?.fields?.map((field: FormField, index: number) => (
                  <motion.div
                    key={field.id}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.05 }}
                    className="flex items-center p-4 bg-dark/30 rounded-lg border border-gray-800 hover:border-gray-700"
                  >
                    <div className="flex-shrink-0 w-8 text-center text-gray-500">
                      {index + 1}
                    </div>
                    <div className="flex-shrink-0 w-10 text-center text-xl">
                      {fieldTypes.find(t => t.value === field.type)?.icon || ''}
                    </div>
                    <div className="flex-1 min-w-0 ml-4">
                      <h4 className="font-medium truncate">{field.label}</h4>
                      <p className="text-sm text-gray-400">
                        {fieldTypes.find(t => t.value === field.type)?.label} 
                        {field.required && <span className="text-red-400 ml-1">*</span>}
                      </p>
                    </div>
                    <div className="flex items-center space-x-2 ml-4">
                      <button
                        onClick={() => handleMoveField(index, 'up')}
                        disabled={index === 0}
                        className="p-1 text-gray-400 hover:text-white disabled:opacity-30"
                      >
                        
                      </button>
                      <button
                        onClick={() => handleMoveField(index, 'down')}
                        disabled={index === (formData.structure?.fields?.length || 0) - 1}
                        className="p-1 text-gray-400 hover:text-white disabled:opacity-30"
                      >
                        
                      </button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          setEditingField(field)
                          setIsFieldModalOpen(true)
                        }}
                      >
                        {t('edit')}
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDeleteField(field.id!)}
                      >
                        {t('delete')}
                      </Button>
                    </div>
                  </motion.div>
                ))}
              </div>
            )}
          </Card>
        </div>
      </div>

      {/* 字段编辑模态框 */}
      <FieldModal
        isOpen={isFieldModalOpen}
        onClose={() => {
          setIsFieldModalOpen(false)
          setEditingField(null)
        }}
        field={editingField}
        fieldTypes={fieldTypes}
        onSave={handleSaveField}
      />
    </DashboardLayout>
  )
}

// 字段编辑模态框
interface FieldModalProps {
  isOpen: boolean
  onClose: () => void
  field: FormField | null
  fieldTypes: { value: string; label: string; icon: string }[]
  onSave: (field: FormField) => void
}

const FieldModal: React.FC<FieldModalProps> = ({ isOpen, onClose, field, fieldTypes, onSave }) => {
  const { t } = useTranslation()
  const [formData, setFormData] = useState<FormField>({
    id: '',
    type: 'text',
    label: '',
    name: '',
    placeholder: '',
    required: false,
    options: [],
    validation: {}
  })

  useEffect(() => {
    if (field) {
      setFormData(field)
    } else {
      setFormData({
        id: '',
        type: 'text',
        label: '',
        name: '',
        placeholder: '',
        required: false,
        options: [],
        validation: {}
      })
    }
  }, [field, isOpen])

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    onSave(formData)
  }

  // 自动生成字段名
  const handleLabelChange = (label: string) => {
    const name = label.toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, '')
    setFormData({ ...formData, label, name })
  }

  // 添加选项
  const addOption = () => {
    const options = [...(formData.options || []), { label: '', value: '' }]
    setFormData({ ...formData, options })
  }

  // 更新选项
  const updateOption = (index: number, key: 'label' | 'value', value: string) => {
    const options = [...(formData.options || [])]
    options[index] = { ...options[index], [key]: value }
    if (key === 'label' && !options[index].value) {
      options[index].value = value.toLowerCase().replace(/\s+/g, '_')
    }
    setFormData({ ...formData, options })
  }

  // 删除选项
  const removeOption = (index: number) => {
    const options = (formData.options || []).filter((_, i) => i !== index)
    setFormData({ ...formData, options })
  }

  const needsOptions = ['select', 'radio', 'checkbox'].includes(formData.type)

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={field ? t('editField') : t('addField')}
      size="lg"
      footer={
        <div className="flex justify-end space-x-2">
          <Button variant="secondary" onClick={onClose}>
            {t('cancel')}
          </Button>
          <Button variant="primary" onClick={handleSubmit}>
            {t('save')}
          </Button>
        </div>
      }
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">
            {t('fieldType')}
          </label>
          <select
            value={formData.type}
            onChange={(e) => setFormData({ ...formData, type: e.target.value as FormField['type'] })}
            className="w-full px-4 py-2 rounded-lg bg-dark/50 border border-gray-700 focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none"
          >
            {fieldTypes.map((type) => (
              <option key={type.value} value={type.value}>
                {type.icon} {type.label}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">
            {t('fieldLabel')} *
          </label>
          <Input
            value={formData.label}
            onChange={(e) => handleLabelChange(e.target.value)}
            placeholder={t('enterFieldLabel')}
            required
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">
            {t('fieldName')} *
          </label>
          <Input
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            placeholder={t('enterFieldName')}
            required
          />
          <p className="text-xs text-gray-500 mt-1">{t('fieldNameHelp')}</p>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">
            {t('placeholder')}
          </label>
          <Input
            value={formData.placeholder}
            onChange={(e) => setFormData({ ...formData, placeholder: e.target.value })}
            placeholder={t('enterPlaceholder')}
          />
        </div>

        <div className="flex items-center space-x-2">
          <input
            type="checkbox"
            id="required"
            checked={formData.required}
            onChange={(e) => setFormData({ ...formData, required: e.target.checked })}
            className="w-4 h-4 text-primary border-gray-600 rounded focus:ring-primary"
          />
          <label htmlFor="required" className="text-sm text-gray-300">
            {t('requiredField')}
          </label>
        </div>

        {needsOptions && (
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              {t('options')}
            </label>
            <div className="space-y-2">
              {(formData.options || []).map((option, index) => (
                <div key={index} className="flex items-center space-x-2">
                  <Input
                    value={option.label}
                    onChange={(e) => updateOption(index, 'label', e.target.value)}
                    placeholder={t('optionLabel')}
                    className="flex-1"
                  />
                  <Input
                    value={option.value}
                    onChange={(e) => updateOption(index, 'value', e.target.value)}
                    placeholder={t('optionValue')}
                    className="flex-1"
                  />
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => removeOption(index)}
                  >
                    {t('delete')}
                  </Button>
                </div>
              ))}
              <Button
                variant="secondary"
                size="sm"
                onClick={addOption}
              >
                {t('addOption')}
              </Button>
            </div>
          </div>
        )}
      </form>
    </Modal>
  )
}

export default FormEditor
